// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormTextField } from '@/components/form-text-field';
import {
  ArrowForwardOutlined,
  Inventory2Outlined,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  ShipDialogFormData,
  ShipDialogItemError,
} from '@/shipment/components/outbound-ship-dialog';
import { getSerialStatus } from '@/shipment/utilities/serial-status';
import { shipmentLineAllowsSerials } from '@/shipment/utilities/shipment-serials';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { Box, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import { Control, UseFormStateReturn, UseFormTrigger } from 'react-hook-form';

interface Props {
  index: number;
  item: ShipDialogFormData['items'][number];
  watchedItem: ShipDialogFormData['items'][number] | undefined;
  control: Control<ShipDialogFormData>;
  formState: UseFormStateReturn<ShipDialogFormData>;
  isEditMode: boolean;
  onSerialDrawerOpen: (index: number | null) => void;
  serialDrawerOpen: number | null;
  onClearErrors: (index: number) => void;
  trigger: UseFormTrigger<ShipDialogFormData>;
  invalidSerials?: Set<string>;
  shipWarehouse: { id: string; name: string } | null;
  inventoryMap: Map<string, Map<string, number>>;
  isLoadingInventory: boolean;
  isFetchingInventory: boolean;
  hasAttemptedSubmit: boolean;
}

export function ShipDialogExistingItem(props: Props) {
  const shouldRenderSerialFields = shipmentLineAllowsSerials(
    props.watchedItem?.item
  );

  const invalidSerialCount = props.invalidSerials?.size ?? 0;
  const itemError = props.formState.errors.items?.[props.index] as
    | ShipDialogItemError
    | undefined;
  const hasFormError = !!itemError?.serials;

  // Check for insufficient inventory
  // Note: We check isLoadingInventory for UI display to avoid flickering,
  // but isFetchingInventory should also block validation to prevent race conditions
  // Bulk items (!hasAssets) require inventory validation
  const hasInsufficientInventory = (() => {
    if (
      props.isEditMode ||
      !props.watchedItem?.item ||
      props.watchedItem.item.hasAssets || // Only check bulk items
      !props.shipWarehouse ||
      props.isLoadingInventory ||
      props.isFetchingInventory
    ) {
      return false;
    }

    const requestedQty = props.watchedItem.quantity ?? 0;
    const availableQty =
      props.inventoryMap
        .get(props.shipWarehouse.id)
        ?.get(props.watchedItem.item.id) ?? 0;

    return requestedQty > availableQty;
  })();

  const hasError =
    (props.hasAttemptedSubmit && invalidSerialCount > 0) ||
    (props.hasAttemptedSubmit && hasFormError) ||
    (props.hasAttemptedSubmit && hasInsufficientInventory);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: hasError ? 'error.main' : 'divider',
        borderWidth: hasError ? 2 : 1,
      }}
    >
      <Stack gap={2}>
        <Stack direction="row" gap={3}>
          {/* Left column - Item info (60%) */}
          <Stack gap={1} sx={{ width: '60%' }}>
            <Stack direction="row" alignItems="center" gap={1}>
              {props.item.item?.hasAssets ? (
                <Inventory2Outlined sx={{ fontSize: '1.25rem' }} />
              ) : (
                <SettingsOutlined sx={{ fontSize: '1.25rem' }} />
              )}
              <Typography variant="subtitle1">
                {props.item.item?.sku}
              </Typography>
              {shouldRenderSerialFields && (
                <Chip
                  label="serialized"
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {props.item.item?.name}
            </Typography>
          </Stack>
          {/* Right column - Expected/Received (40%) */}
          <Stack gap={1} sx={{ width: '40%' }}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '70px' }}
              >
                Expected:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'right',
                  flex: 1,
                  pr: 1.5,
                }}
              >
                {formatNumber(props.item.initialQuantity ?? 0)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="flex-start" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '70px', mt: '8px' }}
              >
                Shipped:
              </Typography>
              <ConditionalTooltip
                arrow
                tooltipText={
                  props.isEditMode
                    ? 'Shipped quantities cannot be modified after ship confirmation'
                    : undefined
                }
                sx={{ flex: 1 }}
              >
                <Stack sx={{ width: '100%' }} gap={0.5}>
                  {/*
                    Note: We suppress the error helper text here because the
                    "Insufficient inventory" message is shown below the Reference ID field.
                    The red outline on the Paper component provides the visual error indicator.
                    However, form submission is still blocked by the Zod schema validation.
                  */}
                  <Box
                    sx={{
                      width: '100%',
                      '& input': {
                        textAlign: 'right',
                      },
                      '& .MuiFormHelperText-root': {
                        display: 'none',
                      },
                      '& .MuiFormControl-root': {
                        marginBottom: 0,
                      },
                      // Conditionally hide error state if user hasn't attempted submit
                      ...(!props.hasAttemptedSubmit && {
                        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
                          {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                        '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline':
                          {
                            borderColor: 'rgba(0, 0, 0, 0.87)',
                          },
                      }),
                    }}
                  >
                    <FormNumberTextField
                      nullable
                      numeric
                      control={props.control}
                      name={`items.${props.index}.quantity`}
                      disabled={props.isEditMode}
                      onChange={() => {
                        props.onClearErrors(props.index);
                        props.trigger(`items.${props.index}.quantity`);
                      }}
                    />
                  </Box>
                  {!props.isEditMode &&
                    props.watchedItem?.item &&
                    !props.watchedItem.item.hasAssets && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'right', pr: 1.5, minHeight: '1.2em' }}
                      >
                        {(() => {
                          if (!props.watchedItem.item) {
                            return '\u00A0'; // Non-breaking space to maintain height
                          }
                          if (!props.shipWarehouse) {
                            return 'Select shipping location first';
                          }
                          if (
                            props.isLoadingInventory ||
                            props.isFetchingInventory
                          ) {
                            return 'Loading...';
                          }
                          const availableQty =
                            props.inventoryMap
                              .get(props.shipWarehouse.id)
                              ?.get(props.watchedItem.item.id) ?? 0;
                          return `${formatNumber(availableQty)} available`;
                        })()}
                      </Typography>
                    )}
                </Stack>
              </ConditionalTooltip>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" gap={3} alignItems="center">
          <Box sx={{ width: '60%' }}>
            <Box sx={{ width: '70%' }}>
              <FormTextField
                fullWidth
                label="Reference ID (optional)"
                placeholder="Ex: batch number"
                control={props.control}
                name={`items.${props.index}.externalReference`}
              />
            </Box>
          </Box>
          <Box
            sx={{
              width: '40%',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              minHeight: '56px',
            }}
          >
            {props.hasAttemptedSubmit &&
              hasInsufficientInventory &&
              props.watchedItem?.item &&
              props.shipWarehouse && (
                <Typography
                  variant="body2"
                  color="error.main"
                  sx={{ textAlign: 'right' }}
                >
                  Insufficient inventory
                </Typography>
              )}
            {shouldRenderSerialFields &&
              props.isEditMode &&
              props.watchedItem?.serials &&
              props.watchedItem.serials.length > 0 && (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() =>
                    props.onSerialDrawerOpen(
                      props.serialDrawerOpen === props.index
                        ? null
                        : props.index
                    )
                  }
                  sx={{
                    textAlign: 'right',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    width: '100%',
                    cursor: 'pointer',
                  }}
                >
                  View {props.watchedItem.serials.length}{' '}
                  {pluralize(props.watchedItem.serials.length, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: false,
                  })}
                  <ArrowForwardOutlined fontSize="small" />
                </Link>
              )}
            {shouldRenderSerialFields &&
              !props.isEditMode &&
              (() => {
                const serialCount = props.watchedItem?.serials?.length || 0;
                const expectedQuantity = props.watchedItem?.quantity || 0;
                const hasError = !!itemError?.serials;

                const { text, icon, color } = getSerialStatus(
                  serialCount,
                  expectedQuantity,
                  invalidSerialCount,
                  hasError,
                  props.hasAttemptedSubmit
                );

                return (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() =>
                      props.onSerialDrawerOpen(
                        props.serialDrawerOpen === props.index
                          ? null
                          : props.index
                      )
                    }
                    sx={{
                      textAlign: 'right',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      width: '100%',
                      cursor: 'pointer',
                      color: color,
                    }}
                  >
                    {text}
                    {icon}
                  </Link>
                );
              })()}
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
